/**
 * @fileoverview Module for `src/lib/routes/UserChecker.js`.
 */

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';

import Loading from '../../components/Loading';
import Page from '../../page/Page';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../queries/queries';

/**
 * Guards protected routes until user profile data is loaded.
 *
 * Redirects unauthenticated users to `/` (or to login if redirectToLogin
 * is true) and renders loading UI while the current user query is pending.
 *
 * @param {Object} params
 * @param {Function} params.component - Protected component to render.
 * @param {boolean} [params.redirectToLogin=false] - If true, redirects
 *   unauthenticated users to the login flow instead of `/`.
 * @returns {JSX.Element} Redirect, loading shell, or protected component.
 */
function UserChecker({ component: Component, redirectToLogin }) {
  const auth = useAuth();
  const match = useRouteMatch();
  const [loginError, setLoginError] = useState(false);
  const [tokenRefreshAttempted, setTokenRefreshAttempted] = useState(false);
  const [tokenRefreshFailed, setTokenRefreshFailed] = useState(false);

  // Check if user appears to be authenticated before fetching user data
  // Note: isAuthenticated() only checks localStorage.expires_at, so users with
  // valid server sessions but expired local tokens will initially appear unauthenticated
  const isAuthenticated = auth.isAuthenticated();

  // Only fetch user data if authenticated - otherwise useUser triggers
  // getAccessToken which redirects to / on failure
  const { data: user, isLoading } = useUser({
    enabled: isAuthenticated,
  });

  // Clear error states when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setLoginError(false);
      setTokenRefreshAttempted(false);
      setTokenRefreshFailed(false);
    }
  }, [isAuthenticated]);

  // Attempt silent token refresh for users who may have valid server sessions
  // but expired localStorage tokens. Only runs once per mount.
  useEffect(() => {
    if (!isAuthenticated && !tokenRefreshAttempted) {
      setTokenRefreshAttempted(true);
      auth.tryRefreshToken()
        .then((refreshed) => {
          if (refreshed) {
            // Trigger a re-render to pick up updated local auth state after refresh.
            setTokenRefreshAttempted(false);
            return undefined;
          }

          // Token refresh failed
          setTokenRefreshFailed(true);

          // For redirectToLogin routes, proceed with login redirect
          if (redirectToLogin) {
            auth.rememberLocation();
            return auth.login(false);
          }

          // For non-redirectToLogin routes, the redirect to / will happen in render
          return undefined;
        })
        .catch(() => {
          // Handle errors from tryRefreshToken or login
          setTokenRefreshFailed(true);
          setLoginError(true);
        });
    }
  }, [isAuthenticated, redirectToLogin, tokenRefreshAttempted, auth]);

  // If not authenticated, handle redirect or show loading during token refresh
  if (!isAuthenticated) {
    // Show loading while token refresh is in progress
    if (!tokenRefreshFailed) {
      return (
        <Page>
          <Loading />
        </Page>
      );
    }

    if (!redirectToLogin) {
      return <Redirect to='/' />;
    }

    // Token refresh failed
    if (redirectToLogin && !loginError) {
      // Show loading while login redirect is in progress
      return (
        <Page>
          <Loading />
        </Page>
      );
    }

    // Login failed or non-redirectToLogin routes: redirect to home
    return <Redirect to='/' />;
  }

  // User is authenticated, wait for user data to load
  if (isLoading) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  if (!user?.auth0_id) {
    // Authenticated but no user data - redirect to home
    return <Redirect to='/' />;
  }

  return <Component auth={ auth } match={ match } user={ user } />;
}

UserChecker.propTypes = {
  component: PropTypes.func.isRequired,
  redirectToLogin: PropTypes.bool,
};

UserChecker.defaultProps = {
  redirectToLogin: false,
};

export default UserChecker;
